import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Contractor } from '../models/contractor.model';
import { User } from '../models/user.model';
import { Roles } from '../utility/role';
import { hasApplicationAccess } from '../utility/site-activation';
import { AppSessionState } from './global-state.models';

const initialState: AppSessionState = {
  currentUser: null,
  contractor: null,
  isLicenseExpired: true,
  authenticationStatus: 'idle',
  contractorStatus: 'idle',
  bootstrapStatus: 'idle',
  error: null,
};

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown global state error';
}

function getRole(user: User | null) {
  if (user?.rights?.supervisor) {
    return Roles.supervisor;
  }
  if (user?.rights?.premiumPartner) {
    return Roles.premiumPartner;
  }
  return user?.role ?? null;
}

export const AppSessionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ currentUser, contractor, authenticationStatus, contractorStatus }) => ({
    role: computed(() => getRole(currentUser())),
    isSupervisor: computed(() => {
      const user = currentUser();
      return user == null ? null : [Roles.admin, Roles.superAdmin].includes(user.role) || !!user.rights?.supervisor;
    }),
    isAuthenticated: computed(() => currentUser() != null),
    hasApplicationAccess: computed(() => hasApplicationAccess(currentUser())),
    isPendingSiteActivation: computed(() => currentUser()?.accountAccessStatus === 'pending-site-activation'),
    contractorGuid: computed(() => contractor()?.guid ?? null),
    contextReady: computed(() => authenticationStatus() === 'loaded' && contractorStatus() !== 'loading'),
  })),
  withMethods((store) => ({
    startAuthentication() {
      patchState(store, { authenticationStatus: 'loading', bootstrapStatus: 'loading', error: null });
    },
    setCurrentUser(currentUser: User | null) {
      if (store.currentUser() === currentUser && store.authenticationStatus() === 'loaded') {
        return;
      }
      patchState(store, { currentUser, authenticationStatus: 'loaded', error: null });
    },
    startContractorLoad() {
      patchState(store, { contractorStatus: 'loading', error: null });
    },
    setContractor(contractor: Contractor | null) {
      if (store.contractor() === contractor && store.contractorStatus() === 'loaded') {
        return;
      }
      patchState(store, { contractor, contractorStatus: 'loaded', error: null });
    },
    setLicenseExpired(isLicenseExpired: boolean) {
      patchState(store, { isLicenseExpired });
    },
    completeBootstrap() {
      patchState(store, { bootstrapStatus: 'loaded', error: null });
    },
    failAuthentication(error: unknown) {
      patchState(store, {
        authenticationStatus: 'error',
        bootstrapStatus: 'error',
        error: getErrorMessage(error),
      });
    },
    failContractor(error: unknown) {
      patchState(store, {
        contractorStatus: 'error',
        bootstrapStatus: 'error',
        error: getErrorMessage(error),
      });
    },
    reset() {
      patchState(store, initialState);
    },
  })),
);
